/**
 * Wine List - Consolidated JavaScript
 *
 * Handles API-powered wine list pages for all Parcelle locations.
 * Features: fetch & render categories, table of contents, fuzzy search,
 * view-more/less toggles, price formatting, and scroll-to-category.
 */
(function () {
  'use strict';

  var DEBOUNCE_DELAY = 300;
  var INITIAL_VISIBLE_COUNT = 15;
  var SCROLL_DELAY = 150;

  /**
   * Format a numeric price as USD currency string.
   * @param {string|number} price
   * @returns {string}
   */
  function formatPrice(price) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(parseFloat(price));
  }

  /**
   * Sanitize a string into a URL-safe anchor ID.
   * @param {string} str
   * @returns {string}
   */
  function toAnchorId(str) {
    return str.replace(/\s+/g, '').toLowerCase();
  }

  /**
   * Sanitize a wine title into a unique element ID.
   * @param {string} title
   * @returns {string}
   */
  function toWineId(title) {
    return title.replace(/[\s\-\(\),\.'"\/!@#$%^&*;:]/g, '_');
  }

  /**
   * Escape HTML entities to prevent XSS.
   * @param {string} str
   * @returns {string}
   */
  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  /**
   * Initialize a single wine list container.
   * @param {HTMLElement} container
   */
  function initWineList(container) {
    var apiEndpoint = container.dataset.apiEndpoint;
    var locationId = container.dataset.locationId;
    var tocEl = container.querySelector('[data-wine-toc]');
    var listEl = container.querySelector('[data-wine-categories]');
    var searchInput = container.querySelector('[data-wine-search]');
    var resultsList = container.querySelector('[data-wine-results]');
    var noResultMsg = container.querySelector('[data-wine-no-results]');
    var loadingEl = container.querySelector('[data-wine-loading]');

    if (!apiEndpoint || !locationId) return;

    var host = apiEndpoint;

    /**
     * Build and inject the table of contents from API categories.
     * @param {Array} categories
     */
    function renderTableOfContents(categories) {
      if (!tocEl) return;

      var html = '<h2 class="wine-list__toc-title">Table of Contents</h2>';
      var count = 1;

      categories.forEach(function (cat) {
        var id = toAnchorId(cat.title);
        html +=
          '<a class="wine-list__toc-item" href="#' + id + '">' +
            '<span class="wine-list__toc-name">' + escapeHtml(cat.title) + '</span>' +
            '<span class="wine-list__toc-dots"></span>' +
            '<span class="wine-list__toc-num">' + count + '</span>' +
          '</a>';
        count++;
      });

      tocEl.innerHTML = html;
    }

    /**
     * Build and inject category sections with wine items.
     * @param {Array} categories
     * @param {Array} wines
     */
    function renderWineCategories(categories, wines) {
      if (!listEl) return;

      var allHTML = '';

      categories.forEach(function (category) {
        var categoryId = toAnchorId(category.title);

        // Filter wines matching this category
        var matching = wines.filter(function (wine) {
          var wineCats = String(wine.categories || '')
            .toLowerCase()
            .split(',')
            .map(function (c) { return c.trim(); });
          return wineCats.includes(String(category.key || '').toLowerCase());
        });

        if (matching.length === 0) return;

        // Sort by price ascending
        matching.sort(function (a, b) {
          return parseFloat(a.price) - parseFloat(b.price);
        });

        var html =
          '<div class="wine-list__category">' +
            '<div class="wine-list__anchor" id="' + categoryId + '"></div>' +
            '<h2 class="wine-list__category-title">' + escapeHtml(category.title) + '</h2>' +
            '<div class="wine-list__divider"></div>' +
            '<ul class="wine-list__wines">';

        // Render initial visible wines
        matching.slice(0, INITIAL_VISIBLE_COUNT).forEach(function (wine) {
          var wineId = toWineId(wine.title);
          html +=
            '<li class="wine-list__wine-item" id="' + wineId + '">' +
              '<span class="wine-list__wine-name">' + escapeHtml(wine.title) + '</span>' +
              '<span class="wine-list__wine-dots"></span>' +
              '<span class="wine-list__wine-price">' + formatPrice(wine.price) + '</span>' +
            '</li>';
        });

        html += '</ul>';

        // Render overflow wines in a collapsible container
        if (matching.length > INITIAL_VISIBLE_COUNT) {
          html += '<ul class="wine-list__wines wine-list__wines--hidden" data-expandable>';

          matching.slice(INITIAL_VISIBLE_COUNT).forEach(function (wine) {
            var wineId = toWineId(wine.title);
            html +=
              '<li class="wine-list__wine-item" id="' + wineId + '">' +
                '<span class="wine-list__wine-name">' + escapeHtml(wine.title) + '</span>' +
                '<span class="wine-list__wine-dots"></span>' +
                '<span class="wine-list__wine-price">' + formatPrice(wine.price) + '</span>' +
              '</li>';
          });

          html += '</ul>';
          html +=
            '<button class="wine-list__view-more" type="button" data-category="' + escapeHtml(category.title) + '">' +
              'View More ' + escapeHtml(category.title) +
            '</button>';
        }

        html += '</div>';
        allHTML += html;
      });

      listEl.innerHTML = allHTML;
      bindViewMoreButtons();
    }

    /**
     * Attach click handlers to all View More / View Less buttons.
     */
    function bindViewMoreButtons() {
      var buttons = container.querySelectorAll('.wine-list__view-more');

      buttons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var expandable = btn.previousElementSibling;
          if (!expandable || !expandable.hasAttribute('data-expandable')) return;

          var catName = btn.dataset.category;

          if (expandable.classList.contains('wine-list__wines--expanded')) {
            expandable.classList.remove('wine-list__wines--expanded');
            expandable.style.maxHeight = '0px';
            btn.textContent = 'View More ' + catName;
          } else {
            expandable.classList.add('wine-list__wines--expanded');
            expandable.style.maxHeight = expandable.scrollHeight + 'px';
            btn.textContent = 'View Less ' + catName;
          }
        });
      });
    }

    /**
     * Set up debounced search against the wine list API.
     */
    function initSearch() {
      if (!searchInput || !resultsList) return;

      var debounceTimer;

      searchInput.addEventListener('keyup', function (e) {
        clearTimeout(debounceTimer);
        var query = e.target.value.trim();

        if (!query) {
          resultsList.classList.add('wine-list__results--hidden');
          resultsList.innerHTML = '';
          if (noResultMsg) noResultMsg.style.display = 'none';
          return;
        }

        debounceTimer = setTimeout(function () {
          var url = host + '/winelist/' + locationId + '?query=' + encodeURIComponent(query);

          fetch(url)
            .then(function (res) { return res.json(); })
            .then(function (data) {
              if (data.success && data.data.wine && data.data.wine.length > 0) {
                resultsList.innerHTML = data.data.wine.map(function (wine) {
                  var wineId = toWineId(wine.title);
                  return (
                    '<li class="wine-list__result-item">' +
                      '<a href="#' + wineId + '" class="wine-list__result-link" data-wine-target="' + wineId + '">' +
                        escapeHtml(wine.title) +
                      '</a>' +
                      '<span class="wine-list__result-price">' + formatPrice(wine.price) + '</span>' +
                    '</li>'
                  );
                }).join('');

                if (noResultMsg) noResultMsg.style.display = 'none';
              } else {
                resultsList.innerHTML = '';
                if (noResultMsg) noResultMsg.style.display = 'block';
              }

              resultsList.classList.remove('wine-list__results--hidden');
            })
            .catch(function (err) {
              console.error('Wine list search error:', err);
            });
        }, DEBOUNCE_DELAY);
      });

      // Delegate click on search result links
      resultsList.addEventListener('click', function (e) {
        var link = e.target.closest('[data-wine-target]');
        if (!link) return;

        e.preventDefault();
        var targetId = link.dataset.wineTarget;
        ensureWineVisible(targetId);
        resultsList.classList.add('wine-list__results--hidden');
        searchInput.value = '';
      });

      // Close results when clicking outside
      document.addEventListener('click', function (e) {
        if (!e.target.closest('[data-wine-search]') && !e.target.closest('[data-wine-results]')) {
          resultsList.classList.add('wine-list__results--hidden');
        }
      });
    }

    /**
     * Expand a hidden wine item's container and scroll to it.
     * @param {string} wineId
     */
    function ensureWineVisible(wineId) {
      var el = document.getElementById(wineId);
      if (!el) return;

      var hidden = el.closest('[data-expandable]');
      if (hidden && !hidden.classList.contains('wine-list__wines--expanded')) {
        hidden.classList.add('wine-list__wines--expanded');
        hidden.style.maxHeight = hidden.scrollHeight + 'px';

        var btn = hidden.nextElementSibling;
        if (btn && btn.classList.contains('wine-list__view-more')) {
          btn.textContent = 'View Less ' + (btn.dataset.category || '');
        }
      }

      setTimeout(function () {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, SCROLL_DELAY);
    }

    /**
     * Show / hide loading state.
     * @param {boolean} show
     */
    function setLoading(show) {
      if (loadingEl) {
        loadingEl.style.display = show ? 'block' : 'none';
      }
    }

    // Fetch initial wine list data
    setLoading(true);

    fetch(host + '/winelist/' + locationId)
      .then(function (res) { return res.json(); })
      .then(function (data) {
        setLoading(false);

        if (data.success || data.data) {
          renderTableOfContents(data.data.categories);
          renderWineCategories(data.data.categories, data.data.wine);
        }
      })
      .catch(function (err) {
        setLoading(false);
        console.error('Error fetching wine list:', err);

        if (listEl) {
          listEl.innerHTML =
            '<p class="wine-list__error">Unable to load the wine list. Please try again later.</p>';
        }
      });

    initSearch();
  }

  // Initialize all wine list containers on the page
  document.addEventListener('DOMContentLoaded', function () {
    var containers = document.querySelectorAll('[data-wine-list]');
    containers.forEach(initWineList);
  });
})();
